import { observer } from 'mobx-react';
import { useConfigureSellingLeadForm } from '~/components/Templates/components/FlowTemplates/useConfigureSellingLeadForm';
import { useTranslation } from 'react-i18next';
import is from 'styled-is';
import { NAMESPACES } from '~/i18n/namespaces';
import styled from 'styled-components';
import { flexColumn } from '@honeybook/hbui/dist/styles/mixins';
import HBDropdown from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBSwitch from '@honeybook/hbui/dist/components/HBSwitch/HBSwitch';
import React, { useState } from 'react';
import { LinkButton, LinkButtons, Link } from './PublishedTemplateModalContent';
import colors from '@honeybook/hbui/dist/styles/colors';
import { useRootStore } from '~/stores/useRootStore';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { usePublishedTemplate } from '~/components/Templates/components/FlowTemplates/usePublishedTemplate';
import ImageSparks24 from '@honeybook/icons/dist/icons/ImageSparks24';
import { useGalleryTemplate } from '~/components/Templates/components/FlowTemplates/useGalleryTemplate';
import { IFlowPublicConfiguration } from '~/types/model.types';
import Dollar16 from '@honeybook/icons/dist/icons/Dollar16';
import { DeactivatePublicTemplateModal } from '~/components/Templates/components/FlowTemplates/DeactivatePublicTemplateModal';

interface SellingTemplateConfigurationProps {
    publicConfiguration: IFlowPublicConfiguration;
    isPadded?: boolean;
}

const SellingTemplateModalContent = ({
    publicConfiguration,
    isPadded = false
}: SellingTemplateConfigurationProps) => {
    const {
        templatesUiStore: { selectedTemplate }
    } = useRootStore();
    const { isMediumUp } = useWindowSize();
    const {
        selledLeadFormOptions,
        isPartner,
        isDisabled,
        setSelledByLeadForm
    } = useConfigureSellingLeadForm({ publicConfiguration });
    const {
        copyLink,
        deactivateLink,
        handleActivateToggleChange,
        isDeactivateModalOpen,
        setIsDeactivateModalOpen
    } = usePublishedTemplate({
        publicConfiguration
    });
    const { generateThumbnails, toggleGalleryTemplate } = useGalleryTemplate({
        publicConfiguration
    });
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const { t: tPartners } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template.partners'
    });
    const [isSelled, setIsSelled] = useState(
        !!publicConfiguration?.selled_by_lead_form_id
    );
    const isPublishedTemplate = !!publicConfiguration?.published_template;

    if (!isPartner) {
        return null;
    }
    const fontSize = isMediumUp ? 'body14' : 'body16';
    const selectedTemplateId = selectedTemplate?._id.toString();

    const toggleSellingLeadForm = async () => {
        handleActivateToggleChange();
        if (!isSelled) {
            setIsSelled(true);
            toggleGalleryTemplate();
        }
    };

    const deactivateSellingTemplate = async () => {
        if (!!publicConfiguration?.selled_by_lead_form_id) {
            await setSelledByLeadForm(null);
        }
        await toggleGalleryTemplate();
        await deactivateLink();
        setIsSelled(false);
    };

    return (
        <StyledModalContent isPadded={isPadded}>
            <SellInHBContainer>
                <HBText type="emphasis16">
                    <SellToggleText>
                        <Dollar16 color="netural900" />
                        {tPartners('sell_toggle')}
                    </SellToggleText>
                </HBText>
                <HBSwitch
                    label={tPartners('sell_toggle')}
                    name={tPartners('sell_toggle')}
                    checked={isPublishedTemplate}
                    onChange={toggleSellingLeadForm}
                    disabled={isDisabled}
                    disabledTooltipText=""
                    testIdName="sell-template"
                    size="md"
                />
            </SellInHBContainer>
            {isPublishedTemplate && (
                <AdvancedConfig>
                    <SellingLeadFormConfig>
                        <HBText type="body14" color="neutral900">
                            {tPartners('choose_lead_form')}
                        </HBText>
                        <HBDropdown
                            id="selled-by-lead-form-toggle"
                            isMulti={false}
                            onChange={setSelledByLeadForm}
                            options={selledLeadFormOptions}
                            value={publicConfiguration?.selled_by_lead_form_id}
                            disabled={isDisabled}
                        />
                    </SellingLeadFormConfig>
                    <SellingLeadFormConfig>
                        <HBText
                            color="neutral900"
                            type="body14"
                            children={t('gallery_tab.template_id_title')}
                        />
                        <LinkButtons>
                            <StyledColumnedLinkContainer>
                                <Link type={fontSize}>
                                    {selectedTemplateId}
                                </Link>
                            </StyledColumnedLinkContainer>
                            <LinkButton
                                id="template_id"
                                onClick={() => {
                                    copyLink(selectedTemplateId);
                                }}
                                size="md"
                                customTheme={{
                                    fontSize
                                }}
                            >
                                {t('copy_cta')}
                            </LinkButton>
                        </LinkButtons>
                    </SellingLeadFormConfig>
                    {!!publicConfiguration?.selled_lead_form_url && (
                        <SellingLeadFormConfig>
                            <HBText
                                color="neutral900"
                                type="body14"
                                children={t(
                                    'gallery_tab.partners.get_template_title'
                                )}
                            />
                            <LinkButtons>
                                <StyledColumnedLinkContainer>
                                    <Link type={fontSize}>
                                        {
                                            publicConfiguration?.selled_lead_form_url
                                        }
                                    </Link>
                                </StyledColumnedLinkContainer>
                                <LinkButton
                                    id="preview_template_url"
                                    onClick={() => {
                                        copyLink(
                                            publicConfiguration?.selled_lead_form_url
                                        );
                                    }}
                                    size="md"
                                    customTheme={{
                                        fontSize
                                    }}
                                >
                                    {t('copy_cta')}
                                </LinkButton>
                            </LinkButtons>
                        </SellingLeadFormConfig>
                    )}
                    <SellingLeadFormConfig>
                        <HBText
                            color="neutral900"
                            type="body14"
                            children={t(
                                'gallery_tab.partners.direct_access_link'
                            )}
                        />
                        <LinkButtons>
                            <StyledColumnedLinkContainer>
                                <Link type={fontSize}>
                                    {publicConfiguration?.public_url}
                                </Link>
                            </StyledColumnedLinkContainer>
                            <LinkButton
                                id="direct_access_link"
                                onClick={() => {
                                    copyLink(publicConfiguration?.public_url);
                                }}
                                size="md"
                                customTheme={{
                                    fontSize
                                }}
                            >
                                {t('copy_cta')}
                            </LinkButton>
                        </LinkButtons>
                    </SellingLeadFormConfig>
                    <GenerateThumnailsButton>
                        <SellingLeadFormConfig>
                            <LinkButtons>
                                <LinkButton
                                    id="generate_thumbnails"
                                    onClick={generateThumbnails}
                                    size="md"
                                    customTheme={{
                                        fontSize
                                    }}
                                    icon={
                                        <ImageSparks24 color={colors.white} />
                                    }
                                >
                                    {t('gallery_tab.generate_title')}
                                </LinkButton>
                            </LinkButtons>
                        </SellingLeadFormConfig>
                    </GenerateThumnailsButton>
                </AdvancedConfig>
            )}
            <DeactivatePublicTemplateModal
                deactivateLink={deactivateSellingTemplate}
                isOpen={isDeactivateModalOpen}
                closeDeactivateModal={() => setIsDeactivateModalOpen(false)}
            />
        </StyledModalContent>
    );
};

export default observer(SellingTemplateModalContent);

const AdvancedConfig = styled.div`
    width: 100%;
    ${flexColumn}
    gap: 16px;
`;

const SellInHBContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    gap: 16px;
`;

const SellingLeadFormConfig = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StyledColumnedLinkContainer = styled.div`
    background-color: ${colors.neutral100};
    border-radius: 4px;
    align-content: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;
    overflow: hidden;
    width: -webkit-fill-available;
`;

const StyledModalContent = styled.div<{ isPadded: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: 100%;
    ${is('isPadded')`
        padding: 32px;
    `}
`;

const SellToggleText = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const GenerateThumnailsButton = styled.div`
    padding-top: 8px;
`;
